/**
 * Paginated school classes hook for Select components.
 */

import { useState, useCallback, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getSchoolClasses } from '@/services/establishments.service';
import type { SelectOption } from '@/components/forms/Select';

/** Options for {@link useSchoolClassesSelect}. */
interface UseSchoolClassesSelectOptions {
  /** Filter by establishment. */
  establishmentId?: string;
  /** Filter by school year. */
  schoolYearId?: string;
  /** Filter by level. */
  levelId?: string;
  /** Free-text search. */
  search?: string;
  /** Gate the query. */
  enabled?: boolean;
}

/**
 * Load school classes with infinite scroll support for Select components.
 * Accumulates pages as user scrolls.
 */
export const useSchoolClassesSelect = (options?: UseSchoolClassesSelectOptions) => {
  const [page, setPage] = useState(1);
  const [accumulatedData, setAccumulatedData] = useState<SelectOption[]>([]);

  const { data, isLoading } = useQuery({
    queryKey: [
      'school-classes-select',
      options?.establishmentId,
      options?.schoolYearId,
      options?.levelId,
      options?.search,
      page,
    ],
    queryFn: () =>
      getSchoolClasses({
        page,
        pageSize: 10,
        establishmentId: options?.establishmentId,
        schoolYearId: options?.schoolYearId,
        levelId: options?.levelId,
        search: options?.search,
      }),
    enabled: options?.enabled !== false,
  });

  /** Merge new page data into accumulated list. */
  useMemo(() => {
    if (!data?.data) return;
    const newOptions = data.data.map((c) => ({ value: c.id, label: c.name }));

    if (page === 1) {
      setAccumulatedData(newOptions);
    } else {
      setAccumulatedData((prev) => {
        const existingIds = new Set(prev.map((o) => o.value));
        const uniqueNew = newOptions.filter((o) => !existingIds.has(o.value));
        return [...prev, ...uniqueNew];
      });
    }
  }, [data, page]);

  /** Load next page. */
  const onLoadMore = useCallback(() => {
    if (data && page * 10 < data.total) {
      setPage((p) => p + 1);
    }
  }, [data, page]);

  /** Has more pages to load. */
  const hasMore = Boolean(data && page * 10 < data.total);

  return {
    options: accumulatedData,
    isLoading,
    onLoadMore,
    hasMore,
  };
};
